<template>
  <v-dialog
    :value="value"
    width="70%"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card v-if="detail" class="rounded-lg">
      <v-card-title class="title primary--text text--darken-3 px-4 py-2">
        {{ detail.yName }} vs {{ detail.xName }}
        <v-spacer></v-spacer>
        <!-- same button and dialog as DataExplorer/Audiogram.vue -->
        <v-btn
          small
          class="primary mx-2"
          :disabled="detail.paired.x.length === 0"
          :loading="exporting"
          @click="exportPlotDialog = true"
          ><v-icon left>mdi-download</v-icon>Export plot</v-btn
        >
        <v-btn icon @click="$emit('input', false)">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pt-3">
        <v-sheet outlined rounded class="pa-3 mb-3">
          <!-- set on the matrix, so shown here rather than edited -->
          <div v-if="adjusting" class="d-flex align-center flex-wrap mb-2">
            <span class="cd-label mr-2">ADJUSTED FOR</span>
            <v-chip
              v-for="name in covariateNames"
              :key="'cd-cov-' + name"
              small
              label
              class="mr-1"
            >
              {{ name }}
            </v-chip>
          </div>

          <div class="d-flex align-center flex-wrap">
            <div class="cd-stat mr-6">
              <div class="cd-label">
                {{ adjusting ? 'PARTIAL r' : 'PEARSON r' }}
              </div>
              <div :class="['cd-value', { 'cd-sig': isSig(shownPearson) }]">
                {{ shownPearson ? formatR(shownPearson.r) : '—' }}
              </div>
              <div class="cd-caption">
                95% CI {{ ciLabel(shownPearson) }} &middot;
                {{ shownPearson ? formatPValue(shownPearson.p) : '—' }}
              </div>
              <div v-if="adjusting" class="cd-caption">
                unadjusted {{ pearson ? formatR(pearson.r) : '—' }}
              </div>
            </div>

            <div class="cd-stat mr-6">
              <div class="cd-label">
                {{ adjusting ? 'PARTIAL SPEARMAN ρ' : 'SPEARMAN ρ' }}
              </div>
              <div :class="['cd-value', { 'cd-sig': isSig(shownSpearman) }]">
                {{ shownSpearman ? formatR(shownSpearman.rho) : '—' }}
              </div>
              <div class="cd-caption">
                {{ shownSpearman ? formatPValue(shownSpearman.p) : '—' }}
              </div>
              <div v-if="adjusting" class="cd-caption">
                unadjusted {{ spearman ? formatR(spearman.rho) : '—' }}
              </div>
            </div>

            <div class="cd-stat mr-6">
              <div class="cd-label">PAIRS USED</div>
              <div class="cd-value">{{ detail.paired.x.length }}</div>
              <div class="cd-caption">
                {{ detail.excluded }} excluded (one value missing)
              </div>
              <div v-if="adjusting" class="cd-caption">
                {{ detail.dropped }} dropped (a covariate missing)
              </div>
            </div>

            <v-spacer />

            <div v-if="loading" class="cd-caption">computing&hellip;</div>
            <div v-else-if="overall && overall.error" class="cd-caption">
              <v-icon x-small color="#c62828" class="mr-1">
                mdi-alert-circle-outline
              </v-icon>
              {{ overall.error }}
            </div>
            <div v-else-if="failed" class="cd-caption">
              <v-icon x-small color="#c62828" class="mr-1">
                mdi-alert-circle-outline
              </v-icon>
              statistics could not be computed
            </div>
          </div>
        </v-sheet>

        <div class="d-flex align-center flex-wrap">
          <v-select
            :value="stratifyUid"
            :items="stratifyOptions"
            label="Stratify by"
            placeholder="none"
            clearable
            dense
            outlined
            hide-details
            class="cd-select mr-3"
            :disabled="stratifyOptions.length === 0"
            @change="$emit('update:stratifyUid', $event || null)"
          />
          <span v-if="stratifyOptions.length === 0" class="cd-caption">
            no categorical predictor with two groups of at least
            {{ minGroupSize }} subjects
          </span>

          <v-spacer />

          <!-- same control as the forest plots' "log scale" option -->
          <span class="subtitle-1 black--text">Show:</span>

          <v-tooltip v-if="adjusting" top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-btn-toggle
                  v-model="view"
                  mandatory
                  dense
                  color="primary"
                  class="ml-3"
                >
                  <v-btn x-small value="observed">observed</v-btn>
                  <v-btn x-small value="adjusted">adjusted</v-btn>
                </v-btn-toggle>
              </span>
            </template>
            <span v-if="detail.strata">
              Within each group, adjusted values remove what the covariates
              explain, keeping that group's mean. Each group's line matches its
              partial r in the table.
            </span>
            <span v-else>
              Adjusted values remove what the covariates explain, keeping each
              variable's mean. Their fitted line matches the partial r.
            </span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-checkbox
                  :input-value="useLogX"
                  :disabled="!canLogX"
                  label="log x"
                  class="pa-0 ma-0 ml-3"
                  dense
                  hide-details
                  @change="logX = !!$event"
                ></v-checkbox>
              </span>
            </template>
            <span>{{ logTooltip('x-axis', xTitle, canLogX) }}</span>
          </v-tooltip>

          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                <v-checkbox
                  :input-value="useLogY"
                  :disabled="!canLogY"
                  label="log y"
                  class="pa-0 ma-0 ml-3"
                  dense
                  hide-details
                  @change="logY = !!$event"
                ></v-checkbox>
              </span>
            </template>
            <span>{{ logTooltip('y-axis', yTitle, canLogY) }}</span>
          </v-tooltip>
        </div>

        <!-- same controls and wording as ParallelCoordinates' subsampling;
             only offered when the pair is large enough to need it -->
        <div v-if="subsampleOffered" class="d-flex align-center flex-wrap mt-2">
          <v-checkbox
            v-model="subsample"
            label="Subsample:"
            class="pa-0 ma-0"
            dense
            hide-details
          />
          <v-slider
            v-model="subsampleChoice"
            :disabled="!subsample"
            step="1"
            max="4"
            ticks="always"
            :tick-labels="subsampleLabels"
            hide-details
            class="cd-slider ml-3"
          ></v-slider>

          <v-tooltip v-if="subsample" top color="primary">
            <template v-slot:activator="{ on: tooltip }">
              <span v-on="{ ...tooltip }">
                <v-badge
                  color="primary"
                  :content="subsampleLabels[subsampleChoice]"
                  class="pl-3"
                ></v-badge>
              </span>
            </template>
            <span
              >Subsampling enabled: only
              {{ subsampleLabels[subsampleChoice] }} of subjects are shown</span
            >
          </v-tooltip>

          <span class="cd-caption ml-6">
            The statistics and fitted line use all
            {{ detail.paired.x.length }} subjects.
          </span>
        </div>

        <plotly-chart
          v-if="detail.paired.x.length > 0"
          ref="scatter"
          :key="detail.key + '|' + view"
          :data="traces"
          :layout="layout"
          :display-mode-bar="true"
        />
        <div
          v-else
          class="title primary--text text--lighten-5 py-5"
          align="center"
        >
          {{ emptyMessage }}
        </div>

        <div v-if="stratifiedAdjusted" class="cd-caption">
          Adjusted within each group.
          <template v-if="hiddenCount > 0">
            {{ hiddenCount }} subject{{ hiddenCount === 1 ? '' : 's' }} outside
            the groups shown {{ hiddenCount === 1 ? 'is' : 'are' }} hidden.
          </template>
        </div>

        <template v-if="detail.strata">
          <div class="text-subtitle-1 font-weight-bold mt-2">
            Same pair, stratified by {{ detail.stratifyName }}
          </div>
          <div v-if="adjusting" class="cd-caption">{{ strataCaption }}</div>

          <v-simple-table dense class="cd-table">
            <thead>
              <tr>
                <th>{{ detail.stratifyName }}</th>
                <th class="text-right">n</th>
                <th class="text-right">
                  {{ strataPartial ? 'partial r' : 'Pearson r' }}
                </th>
                <th class="text-right">95% CI</th>
                <th class="text-right">p</th>
                <th class="text-right">
                  {{ strataPartial ? 'partial ρ' : 'Spearman ρ' }}
                </th>
                <th class="text-right">p</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in strataRows" :key="'cd-s-' + row.level">
                <td>
                  <span
                    class="cd-dot"
                    :style="{ backgroundColor: row.color }"
                  ></span>
                  {{ row.level }}
                  <v-icon
                    v-if="row.reversed"
                    x-small
                    color="#c62828"
                    class="ml-1"
                    title="r points the other way from the overall r"
                  >
                    mdi-swap-vertical
                  </v-icon>
                </td>
                <td class="text-right">{{ row.n }}</td>
                <template v-if="row.stats && !row.stats.error">
                  <td class="text-right cd-mono">
                    {{ formatR(row.stats.pearson.r) }}
                  </td>
                  <td class="text-right cd-mono">
                    {{ ciLabel(row.stats.pearson) }}
                  </td>
                  <td class="text-right cd-mono">
                    {{ formatPValue(row.stats.pearson.p) }}
                  </td>
                  <td class="text-right cd-mono">
                    {{ formatR(row.stats.spearman.rho) }}
                  </td>
                  <td class="text-right cd-mono">
                    {{ formatPValue(row.stats.spearman.p) }}
                  </td>
                </template>
                <td v-else colspan="5" class="text-right cd-caption">
                  {{
                    row.stats ? row.stats.error : loading ? 'computing…' : '—'
                  }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <div v-if="reversedLevels.length > 0" class="cd-caption mt-1">
            <v-icon x-small color="#c62828" class="mr-1">
              mdi-swap-vertical
            </v-icon>
            The correlation within {{ reversedLevels.join(', ') }} points the
            other way from the overall one. Pooling these groups may be masking
            or reversing the relationship (Simpson's paradox).
            <template v-if="adjusting">
              An adjusted r assumes the relationship is the same in every group,
              which these groups do not bear out.
            </template>
          </div>

          <div v-if="detail.disclosure" class="cd-caption mt-1">
            <v-icon x-small color="#777" class="mr-1">mdi-information</v-icon>
            {{ detail.disclosure }}
          </div>
        </template>

        <div class="cd-caption mt-3">
          {{ valuesNote }} p-values are not corrected for multiple comparisons:
          across a matrix of many pairs, some will fall below 0.05 by chance.
          Treat them as exploratory.
        </div>
      </v-card-text>

      <export-plot-dialog
        :show="exportPlotDialog"
        :default-filename-prefix="exportFilename"
        @dialogClosed="exportPlotDialog = false"
        @exportPlot="exportPlot"
      >
      </export-plot-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import { colors } from '@/utils/colors';
import { MIN_GROUP_SIZE, formatPValue } from '@/utils/dashboard';
import {
  SUBSAMPLE_LABELS,
  SUBSAMPLE_STRIDES,
  allPositive,
  defaultSubsampleChoice,
  formatR,
  signReversals,
  subsamplePoints,
} from '@/utils/correlation';
import { downloadPlotlyFigure } from '@/utils/plotlyExport';
import ExportPlotDialog from '@/components/common/ExportPlotDialog.vue';
import PlotlyChart from '@/components/common/PlotlyChart.vue';

// export size when the on-screen plot has no measurable width
const EXPORT_FALLBACK_WIDTH = 900;

// points for subjects outside every shown stratum
const OTHER_COLOR = '#bdbdbd';
const BAND_COLOR = 'rgba(0, 0, 0, 0.12)';

export default {
  name: 'PairDetailDialog',
  components: {
    ExportPlotDialog,
    PlotlyChart,
  },
  props: {
    // dialog open state (v-model)
    value: {
      type: Boolean,
      default: false,
    },
    // {key, pairKey, xName, yName, exportFilename, paired, adjusted, excluded,
    // dropped, strata, strataAdjustedFor, stratifyName, disclosure,
    // visitsInNames}; null while the dialog has never opened
    detail: {
      type: Object,
      default: null,
    },
    // compute-correlation response, once it arrives
    stats: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // true when the request failed outright, as opposed to returning an error
    failed: {
      type: Boolean,
      default: false,
    },
    // [{value: uid, text: name}] of categorical predictors usable as strata
    stratifyOptions: {
      type: Array,
      default: () => [],
    },
    stratifyUid: {
      type: String,
      default: null,
    },
    // display names of the covariates set on the matrix; empty when unadjusted
    covariateNames: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      minGroupSize: MIN_GROUP_SIZE,
      subsampleLabels: SUBSAMPLE_LABELS,
      // the user's choice, kept across pairs; an axis that cannot be logged
      // shows unchecked and ignores it, and picks it back up on the next pair
      // that can
      logX: false,
      logY: false,
      subsample: false,
      subsampleChoice: 0,
      exportPlotDialog: false,
      exporting: false,
      // 'observed' or 'adjusted'; only meaningful while adjusting
      view: 'observed',
    };
  },
  computed: {
    emptyMessage() {
      const which = this.adjusting ? 'EVERY VALUE' : 'BOTH VALUES';
      return this.detail.visitsInNames
        ? `NO SUBJECTS WITH ${which}`
        : `NO SUBJECTS WITH ${which} AT BASELINE`;
    },
    valuesNote() {
      if (!this.detail.visitsInNames)
        return 'Values at baseline, one per subject.';
      return (
        'One value per subject, at the visit shown in each name; a Δ ' +
        'variable is its last-visit value minus its first-visit value. Any ' +
        'variable without a visit in its name is its value at baseline.'
      );
    },
    adjusting() {
      return (
        this.covariateNames.length > 0 &&
        !!this.detail &&
        !!this.detail.adjusted
      );
    },
    adjustedView() {
      return this.adjusting && this.view === 'adjusted';
    },
    // coordinates drawn for every subject, parallel to detail.paired.subjectIds
    shownPoints() {
      if (!this.detail) return { x: [], y: [] };
      return this.adjustedView ? this.detail.adjusted : this.detail.paired;
    },
    // stratify, then adjust: each stratum is drawn from its own adjusted
    // points, and subjects outside the strata are left out
    stratifiedAdjusted() {
      return this.adjustedView && !!this.detail.strata;
    },
    // every point actually on the plot, whichever view
    drawnPoints() {
      if (!this.stratifiedAdjusted) return this.shownPoints;
      const pts = { x: [], y: [] };
      this.detail.strata.forEach(s => {
        pts.x.push(...s.adjusted.x);
        pts.y.push(...s.adjusted.y);
      });
      return pts;
    },
    // subjects in no stratum shown: drawn grey when observed, hidden when
    // adjusted
    hiddenCount() {
      if (!this.detail || !this.detail.strata) return 0;
      const inStrata = this.detail.strata.reduce((n, s) => n + s.x.length, 0);
      return this.detail.paired.x.length - inStrata;
    },
    xTitle() {
      if (!this.detail) return '';
      return this.adjustedView
        ? `${this.detail.xName} (adjusted)`
        : this.detail.xName;
    },
    yTitle() {
      if (!this.detail) return '';
      return this.adjustedView
        ? `${this.detail.yName} (adjusted)`
        : this.detail.yName;
    },
    exportFilename() {
      if (!this.detail) return '';
      return this.adjustedView
        ? `${this.detail.exportFilename}_adjusted`
        : this.detail.exportFilename;
    },
    canLogX() {
      return allPositive(this.drawnPoints.x);
    },
    canLogY() {
      return allPositive(this.drawnPoints.y);
    },
    useLogX() {
      return this.logX && this.canLogX;
    },
    useLogY() {
      return this.logY && this.canLogY;
    },
    subsampleOffered() {
      return (
        !!this.detail &&
        defaultSubsampleChoice(this.detail.paired.x.length) !== null
      );
    },
    stride() {
      return this.subsampleOffered && this.subsample
        ? SUBSAMPLE_STRIDES[this.subsampleChoice]
        : 1;
    },
    overall() {
      return this.stats ? this.stats.overall : null;
    },
    pearson() {
      return this.overall ? this.overall.pearson : null;
    },
    spearman() {
      return this.overall ? this.overall.spearman : null;
    },
    // the headline statistics: partial when adjusting, plain otherwise
    shownPearson() {
      if (!this.adjusting) return this.pearson;
      return this.overall && this.overall.adjusted
        ? this.overall.adjusted.pearson
        : null;
    },
    shownSpearman() {
      if (!this.adjusting) return this.spearman;
      return this.overall && this.overall.adjusted
        ? this.overall.adjusted.spearman
        : null;
    },
    // within a stratum the stratifying variable is constant and is left out of
    // the adjustment, so stratifying by the only covariate leaves plain r
    strataPartial() {
      return (
        this.adjusting &&
        !!this.detail &&
        this.detail.strataAdjustedFor.length > 0
      );
    },
    strataCaption() {
      if (!this.detail) return '';
      if (this.strataPartial) {
        return (
          'Within each group, adjusted for ' +
          `${this.detail.strataAdjustedFor.join(', ')}.`
        );
      }
      return (
        `Within each group, unadjusted: ${this.detail.stratifyName} is ` +
        'constant inside a group, so there is nothing left to adjust for.'
      );
    },
    strataStats() {
      const byLevel = new Map();
      if (this.stats && this.stats.strata) {
        this.stats.strata.forEach(s => byLevel.set(String(s.level), s));
      }
      return byLevel;
    },
    strataRows() {
      if (!this.detail || !this.detail.strata) return [];
      const reversed = new Set(this.reversedLevels);
      return this.detail.strata.map((s, i) => ({
        level: s.level,
        n: s.x.length,
        color: this.strataColor(i),
        stats: this.rowStats(s.level),
        reversed: reversed.has(s.level),
      }));
    },
    // adjusted strata are compared with the adjusted overall r, plain with plain
    reversedLevels() {
      if (!this.detail || !this.detail.strata || !this.shownPearson) return [];
      const strata = this.detail.strata.map(s => {
        const st = this.rowStats(s.level);
        return {
          level: s.level,
          r: st && !st.error && st.pearson ? st.pearson.r : null,
        };
      });
      return signReversals(this.shownPearson.r, strata);
    },
    visibleFit() {
      let fit = this.overall ? this.overall.fit : null;
      if (this.adjustedView) {
        fit =
          this.overall && this.overall.adjusted
            ? this.overall.adjusted.fit
            : null;
      }
      // stratified, the overall line is drawn without its band
      return this.clipFit(fit, !(this.detail && this.detail.strata));
    },
    // one line per stratum, null where the stratum has none to show: too few
    // subjects, or an adjustment it could not support
    strataFits() {
      if (!this.detail || !this.detail.strata) return [];
      return this.detail.strata.map(s => {
        const st = this.strataStats.get(String(s.level));
        if (!st || st.error) return null;
        let fit = st.fit;
        // a stratum left with no covariate is unadjusted, so its plain line
        // already sits on its adjusted points
        if (this.adjustedView && this.strataPartial) {
          fit = st.adjusted ? st.adjusted.fit : null;
        }
        return this.clipFit(fit, false);
      });
    },
    traces() {
      if (!this.detail) return [];
      const traces = [];

      if (this.stratifiedAdjusted) {
        this.detail.strata.forEach((s, i) => {
          traces.push(
            this.pointsTrace(s.adjusted, s.level, this.strataColor(i), i)
          );
        });
      } else if (this.detail.strata) {
        // observed: each stratum's subjects are picked out of the paired
        // coordinates, and those in no stratum shown are drawn grey
        const index = new Map(
          this.detail.paired.subjectIds.map((id, i) => [id, i])
        );
        const drawn = new Set();
        const pick = ids => {
          const pts = { x: [], y: [] };
          ids.forEach(id => {
            const i = index.get(id);
            if (i === undefined) return;
            drawn.add(id);
            pts.x.push(this.shownPoints.x[i]);
            pts.y.push(this.shownPoints.y[i]);
          });
          return pts;
        };

        this.detail.strata.forEach((s, i) => {
          traces.push(
            this.pointsTrace(
              pick(s.subjectIds),
              s.level,
              this.strataColor(i),
              i
            )
          );
        });

        const rest = pick(
          this.detail.paired.subjectIds.filter(id => !drawn.has(id))
        );
        if (rest.x.length > 0) {
          traces.push(this.pointsTrace(rest, 'other / missing', OTHER_COLOR));
        }
      } else {
        traces.push(
          this.pointsTrace(
            this.shownPoints,
            'subjects',
            colors['plotly_palette'][0]
          )
        );
      }

      const fit = this.visibleFit;
      if (fit && this.detail.strata) {
        // a reference for the strata: thin, dashed, and without its band
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: fit.x,
          y: fit.y,
          line: { color: '#000000', width: 1.5, dash: 'dash' },
          name: this.adjustedView
            ? 'all subjects (adjusted together)'
            : 'all subjects',
          hoverinfo: 'skip',
        });
      } else if (fit) {
        // lower edge first, so the upper edge can fill down to it
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: fit.x,
          y: fit.lower,
          line: { width: 0 },
          hoverinfo: 'skip',
          showlegend: false,
        });
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: fit.x,
          y: fit.upper,
          line: { width: 0 },
          fill: 'tonexty',
          fillcolor: BAND_COLOR,
          hoverinfo: 'skip',
          name: '95% CI',
        });
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: fit.x,
          y: fit.y,
          line: { color: '#000000', width: 2 },
          name: 'linear fit',
          hoverinfo: 'skip',
        });
      }

      // after the points, so each line sits on top of its own cloud; grouped
      // with its points, so the legend toggles both
      this.strataFits.forEach((sf, i) => {
        if (!sf) return;
        traces.push({
          type: 'scatter',
          mode: 'lines',
          x: sf.x,
          y: sf.y,
          line: { color: this.strataColor(i), width: 2.5 },
          legendgroup: `stratum-${i}`,
          showlegend: false,
          hoverinfo: 'skip',
        });
      });

      return traces;
    },
    layout() {
      if (!this.detail) return {};
      return {
        height: 440,
        autosize: true,
        showlegend: true,
        legend: { orientation: 'h', y: -0.2 },
        margin: { t: 20, l: 60, r: 20, b: 60 },
        xaxis: {
          title: { text: this.xTitle },
          type: this.useLogX ? 'log' : 'linear',
          zeroline: false,
          automargin: true,
        },
        yaxis: {
          title: { text: this.yTitle },
          type: this.useLogY ? 'log' : 'linear',
          zeroline: false,
          automargin: true,
        },
        hovermode: 'closest',
        hoverlabel: {
          bgcolor: '#ffffff',
          font: { family: 'Roboto, sans-serif', size: 14 },
        },
        font: { family: 'Roboto, sans-serif', size: 12 },
        modebar: { remove: ['lasso', 'select', 'pan'] },
      };
    },
  },
  watch: {
    // a new pair gets the default subsampling for its size; a new stratum on
    // the same pair keeps whatever the user chose
    'detail.pairKey': {
      immediate: true,
      handler() {
        if (!this.detail) return;
        const choice = defaultSubsampleChoice(this.detail.paired.x.length);
        this.subsample = choice !== null;
        this.subsampleChoice = choice === null ? 0 : choice;
      },
    },
  },
  methods: {
    formatR,
    formatPValue,
    // a stratum's statistics as the table shows them: the partial block when
    // that stratum was adjusted, otherwise the plain one
    rowStats(level) {
      const st = this.strataStats.get(String(level));
      if (!st) return null;
      if (!st.adjusted) return st;
      return {
        ...st,
        pearson: st.adjusted.pearson,
        spearman: st.adjusted.spearman,
      };
    },
    // Exports what is on screen -- log axes, subsampling, strata, fit -- at
    // the size it is drawn
    async exportPlot(d) {
      this.exportPlotDialog = false;
      const el = this.$refs.scatter ? this.$refs.scatter.$el : null;
      const width =
        el && el.offsetWidth > 0 ? el.offsetWidth : EXPORT_FALLBACK_WIDTH;

      this.exporting = true;
      try {
        await downloadPlotlyFigure(this.traces, this.layout, {
          format: d.format,
          filename: d.prefix,
          width,
          height: this.layout.height,
        });
      } finally {
        this.exporting = false;
      }
    },
    logTooltip(axis, name, available) {
      if (!available) {
        return `Log scale unavailable: ${name} has values at or below zero`;
      }
      return (
        `Show log scale on ${axis}. Changes the display only; ` +
        'the statistics are computed on the original values.'
      );
    },
    isSig(s) {
      return !!s && s.p != null && s.p < 0.05;
    },
    ciLabel(s) {
      if (!s || !s.ci) return '—';
      return `[${formatR(s.ci[0])}, ${formatR(s.ci[1])}]`;
    },
    strataColor(i) {
      const palette = colors['plotly_palette'];
      return palette[i % palette.length];
    },
    // On a log y-axis, the parts of a line at or below zero would be dropped
    // by Plotly, and with a band break its fill, so a fit is drawn only where
    // its line (and band, if drawn) is positive. The x grid spans the data,
    // which is positive whenever log x is available.
    clipFit(fit, withBand) {
      if (!fit || !this.useLogY) return fit;

      const keep = fit.x
        .map((x, i) => i)
        .filter(
          i =>
            fit.y[i] > 0 &&
            (!withBand || (fit.lower[i] > 0 && fit.upper[i] > 0))
        );
      if (keep.length < 2) return null;
      return {
        x: keep.map(i => fit.x[i]),
        y: keep.map(i => fit.y[i]),
        lower: keep.map(i => fit.lower[i]),
        upper: keep.map(i => fit.upper[i]),
      };
    },
    // WebGL keeps a scatter of tens of thousands of subjects responsive.
    // Subsampling thins each trace by the same stride, so the strata keep
    // their proportions. A stratum's index ties its points to its line in the
    // legend.
    pointsTrace(points, name, color, stratumIndex = null) {
      const shown = subsamplePoints(points, this.stride);
      return {
        type: 'scattergl',
        mode: 'markers',
        x: shown.x,
        y: shown.y,
        name,
        legendgroup:
          stratumIndex === null ? undefined : `stratum-${stratumIndex}`,
        marker: { color, size: 5, opacity: 0.55 },
        hovertemplate: `${name}<br>x=%{x}<br>y=%{y}<extra></extra>`,
      };
    },
  },
};
</script>

<style scoped>
.cd-caption {
  font-size: 12px;
  color: #777;
}
.cd-label {
  font-size: 11px;
  color: #777;
  letter-spacing: 0.6px;
}
.cd-value {
  font-family: monospace;
  font-size: 22px;
  line-height: 1.2;
  color: #333;
}
.cd-sig {
  font-weight: 700;
  color: #c62828;
}
.cd-mono {
  font-family: monospace;
}
.cd-select {
  max-width: 280px;
}
.cd-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 4px;
}
.cd-slider {
  max-width: 260px;
}
.cd-table {
  max-width: 900px;
}
</style>
